import { supabase } from './supabase';
import { databaseService } from './databaseService';
import { SubscriptionServiceLogger } from '../utils/logger';
import { APP_CONSTANTS, getStripeEdgeFunctionName } from '../config/constants';
import { isWeb } from '../utils/platform';
import { globalState, companyActions, userActions } from '../state/globalState';
import { SubscriptionBillingProvider, SubscriptionInfo } from '../types/subscription';
import { v4 as uuidv4 } from 'uuid';
import { getUserCompany } from './userService';
import {
  restoreAppleIapPurchases,
  subscribeWithAppleIap,
  type AppleIapPurchaseResult,
} from './appleIapService';

/**
 * IDENTIFICADORES DE ASSINATURAS
 * 
 * Este serviço lida com dois tipos principais de identificadores:
 * 
 * 1. ID LOCAL (id): UUID gerado pelo Supabase, é a chave primária na tabela guincho_subscriptions
 *    - Usado para operações CRUD no banco de dados local (SQLite/Dexie) e Supabase
 *    - Usado para relacionamentos com outras tabelas (foreign keys)
 *    - SEMPRE usar este ID para atualizações no banco local ou Supabase
 * 
 * 2. EXTERNAL ID (external_subscription_id): Identificador da assinatura no Stripe
 *    - Usado APENAS para comunicação com API do Stripe
 *    - Usado para identificar a assinatura em webhooks e funções Edge
 *    - NÃO usar este ID para atualizações diretas no banco local ou Supabase
 * 
 * Para evitar confusões, usamos a função auxiliar getLocalSubscriptionByExternalId
 * que busca uma assinatura local pelo seu ID externo.
 */

// Loggers específicos para cada parte do serviço
const mainLogger = SubscriptionServiceLogger.Main;
const statusLogger = SubscriptionServiceLogger.Status;
const plansLogger = SubscriptionServiceLogger.Plans;
const paymentsLogger = SubscriptionServiceLogger.Payments;
const subscribeLogger = SubscriptionServiceLogger.Subscribe;
const localDbLogger = SubscriptionServiceLogger.LocalDb;
const remoteDbLogger = SubscriptionServiceLogger.RemoteDb;

const MANUAL_EXTERNAL_ID_PREFIX = 'manual:';
const APPLE_EXTERNAL_ID_PREFIX = APP_CONSTANTS.APPLE_IAP.EXTERNAL_ID_PREFIX;

export const isAppleExternalId = (externalId?: string | null): boolean =>
  typeof externalId === 'string' && externalId.startsWith(APPLE_EXTERNAL_ID_PREFIX);

export const isManualExternalId = (externalId?: string | null): boolean =>
  typeof externalId === 'string' && externalId.startsWith(MANUAL_EXTERNAL_ID_PREFIX);

export const resolveBillingProvider = (externalId?: string | null): SubscriptionBillingProvider => {
  if (isAppleExternalId(externalId)) return 'apple';
  return isManualExternalId(externalId) ? 'manual' : 'stripe';
};

export const resolvePaymentMethod = (externalId?: string | null): 'pix' | 'stripe' | 'apple_iap' | 'unknown' => {
  if (!externalId) return 'unknown';
  if (isAppleExternalId(externalId)) return 'apple_iap';
  return isManualExternalId(externalId) ? 'pix' : 'stripe';
};

const buildFreeSubscription = (empresaId: string, overrides: Partial<SubscriptionInfo> = {}): SubscriptionInfo => ({
  id: '',
  empresa_id: empresaId,
  externalId: '',
  status: 'free',
  planType: 'free',
  planName: '',
  planId: '',
  startDate: null,
  nextBillingDate: null,
  expiration: null,
  lastChecked: new Date().toISOString(),
  billingProvider: 'stripe',
  isManual: false,
  paymentMethod: 'unknown',
  ...overrides,
});

const normalizeDateInput = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
};

/**
 * Interface para representar uma assinatura local no banco de dados
 */
export interface LocalSubscription {
  id: string;                        // UUID local, chave primária no banco
  subscription_plan_id: string;      // Referência ao plano de assinatura
  empresa_id: string;                // Empresa associada à assinatura
  start_date: string | null;         // Data de início
  end_date: string | null;           // Data de término (final do período se cancelado)
  status: 'ativa' | 'cancelamento_agendado' | 'cancelada' | 'free';  // Status da assinatura
  next_billing_date: string | null;  // Data da próxima cobrança
  external_subscription_id: string;  // ID externo (Stripe ou manual:<uuid>)
  created_at?: string;               // Data de criação
  updated_at?: string;               // Data de última atualização
}

/**
 * Interface para representar os dados de uma assinatura retornados pela API do Stripe
 */
export interface StripeSubscriptionDetails {
  id: string;                       // ID da assinatura no Stripe
  status: string;                   // Status da assinatura no Stripe
  currentPeriodEnd: number | null;  // Timestamp do final do período atual
  startDate: string | null;         // Data de início
  nextBillingDate: string | null;   // Data da próxima cobrança
  end_date?: string | null;         // Data de término (presente em assinaturas canceladas)
  error?: string;                   // Erro, se houver
}

interface AdminSubscriptionInput {
  empresaId: string;
  subscriptionPlanId: string;
  status?: LocalSubscription['status'];
  startDate?: string | null;
  nextBillingDate?: string | null;
  endDate?: string | null;
  externalSubscriptionId?: string;
}

interface AdminPaymentInput {
  empresaId: string;
  subscriptionExternalId: string;
  amount: number;
  paymentDate?: string | null;
  status?: string;
  reference?: string;
  subscriptionId?: string;
}

// Função auxiliar para mapear status local/remoto para o contrato único
function mapStatusToContract(status: string): SubscriptionInfo['status'] {
  switch (status) {
    case 'ativa':
      return 'ativa';
    case 'cancelamento_agendado':
      return 'cancelamento_agendado';
    case 'cancelada':
      return 'cancelada';
    case 'free':
      return 'free';
    default:
      return 'free';
  }
}

// Função utilitária para mapear assinatura do banco (local ou remoto) para o contrato SubscriptionInfo
function mapDbSubscriptionToContract(db: any, plan?: any): SubscriptionInfo {
  statusLogger.info('[ASSINATURA] Mapeando assinatura do banco:', db);
  statusLogger.info(`[DEBUG-PAUSE] mapDbSubscriptionToContract - Valor de entrada next_billing_date: ${JSON.stringify(db.next_billing_date)}`);
  const externalId = db.external_subscription_id || '';
  const billingProvider = resolveBillingProvider(externalId);
  const paymentMethod = resolvePaymentMethod(externalId);
  const isManual = billingProvider === 'manual';
  const result: SubscriptionInfo = {
    id: db.id,
    empresa_id: db.empresa_id,
    externalId,
    status: db.status,
    planType: (db.status === 'ativa' || db.status === 'cancelamento_agendado') ? 'premium' : 'free',
    planName: plan?.name || '',
    planId: db.subscription_plan_id,
    startDate: db.start_date ? new Date(db.start_date).toISOString() : null,
    nextBillingDate: (() => {
      if (!db.next_billing_date || db.next_billing_date === '') return null;
      const d = new Date(db.next_billing_date);
      return isNaN(d.getTime()) ? null : d.toISOString();
    })(),
    expiration: db.end_date ? new Date(db.end_date).toISOString() : null,
    lastChecked: new Date().toISOString(),
    billingProvider,
    isManual,
    paymentMethod,
  };
  statusLogger.info(`[DEBUG-PAUSE] mapDbSubscriptionToContract - Valor de saída next_billing_date: ${JSON.stringify(result.nextBillingDate)}`);
  return result;
}

interface NormalizedSubscriptionResult {
  subscription: any;
  wasAdjusted: boolean;
  reason: string | null;
}

/**
 * Normaliza a assinatura para evitar que um usuário permaneça com acesso premium
 * após o vencimento do período pago quando o app está offline.
 */
function normalizeSubscriptionForExpiration(subscription: any): NormalizedSubscriptionResult {
  if (!subscription) {
    return { subscription, wasAdjusted: false, reason: null };
  }

  try {
    const status = subscription.status;
    const now = Date.now();
    const endDateRaw = subscription.end_date;
    const nextBillingRaw = subscription.next_billing_date;

    const endDateTs = endDateRaw ? new Date(endDateRaw).getTime() : null;
    const nextBillingTs = nextBillingRaw ? new Date(nextBillingRaw).getTime() : null;

    const endDateValid = typeof endDateTs === 'number' && !Number.isNaN(endDateTs);
    const nextBillingValid = typeof nextBillingTs === 'number' && !Number.isNaN(nextBillingTs);

    const endDateElapsed = endDateValid && endDateTs !== null && endDateTs <= now;
    const billingElapsed = nextBillingValid && nextBillingTs !== null && nextBillingTs <= now;

    const shouldForceDowngrade = (status === 'ativa' || status === 'cancelamento_agendado') &&
      (endDateElapsed || (status === 'cancelamento_agendado' && billingElapsed));

    if (shouldForceDowngrade) {
      const normalized = {
        ...subscription,
        status: 'cancelada',
        next_billing_date: null,
      };

      return {
        subscription: normalized,
        wasAdjusted: true,
        reason: endDateElapsed ? 'end_date_elapsed' : 'billing_date_elapsed',
      };
    }
  } catch (error) {
    statusLogger.warn('[ASSINATURA] Falha ao normalizar assinatura para expiração offline', error);
  }

  return { subscription, wasAdjusted: false, reason: null };
}

/**
 * Verifica o status da assinatura de uma empresa
 * @param empresaId ID da empresa para verificar
 * @returns Status da assinatura (contrato único)
 */
export const checkSubscriptionStatus = async (
  empresaId: string,
  options: { allowRemote?: boolean } = {}
): Promise<SubscriptionInfo> => {
  statusLogger.info(`[ASSINATURA] Iniciando verificação de status para empresa: ${empresaId}`);

  if (!empresaId) {
    statusLogger.warn('Tentativa de verificar assinatura sem ID de empresa');
    return buildFreeSubscription(empresaId ?? '');
  }

  try {
    // Verificar assinaturas ativas no banco local primeiro
    //localDbLogger.info(`Buscando assinaturas no banco local para empresa: ${empresaId}`);
    // Buscar assinaturas locais
    const localSubscriptions = await databaseService.listItems(
      APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME,
      { empresa_id: empresaId }
    );
    
    localDbLogger.info(`Assinaturas locais encontradas: ${localSubscriptions?.length || 0}`,
      localSubscriptions?.length > 0 ? JSON.stringify(localSubscriptions[0]) : 'nenhuma');

    // Filtrar apenas assinaturas com status ativa ou cancelamento_agendado
    const activeLocalSubscriptions = localSubscriptions?.filter(
      (sub: any) => sub.status === 'ativa' || sub.status === 'cancelamento_agendado'
    );
    statusLogger.info(`[ASSINATURA] Assinaturas locais ativas/cancelamento_agendado: ${activeLocalSubscriptions?.length || 0}`);

    // Se encontrar assinatura ativa local
    if (activeLocalSubscriptions && activeLocalSubscriptions.length > 0) {
      // Ordenar por created_at em ordem decrescente (mais recentes primeiro)
      activeLocalSubscriptions.sort((a: any, b: any) => {
        const dateA = new Date(a.created_at || 0).getTime();
        const dateB = new Date(b.created_at || 0).getTime();
        return dateB - dateA;
      });

      const subscription = activeLocalSubscriptions[0] as any;
      statusLogger.info('[DEBUG-PAUSE] assinatura local encontrada', subscription);
      statusLogger.info(`[DEBUG-PAUSE] checkSubscriptionStatus - next_billing_date (local): ${JSON.stringify(subscription.next_billing_date)}`);
      // Buscar detalhes do plano
      const planId = subscription.subscription_plan_id;
      //localDbLogger.info(`Buscando detalhes do plano: ${planId}`);
      const plan = await databaseService.readItem(
        APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTION_PLANS.NAME,
        planId
      ) as any;

      localDbLogger.info(`Detalhes do plano: ${JSON.stringify(plan)}`);

      const now = new Date().getTime();
      const endDate = subscription.end_date ? new Date(subscription.end_date) : null;
      const nextBillingDate = subscription.next_billing_date;
      const isActive = (subscription.status === 'ativa' || subscription.status === 'cancelamento_agendado')
        && (!endDate || endDate.getTime() > now);

      const { subscription: normalizedSubscription, wasAdjusted, reason } = normalizeSubscriptionForExpiration(subscription);

      if (wasAdjusted) {
        statusLogger.info(`[ASSINATURA] Assinatura local expirada detectada (${reason}). Aplicando downgrade offline-first.`);
        try {
          await databaseService.updateItem(
            APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME,
            normalizedSubscription.id,
            {
              status: normalizedSubscription.status,
              next_billing_date: normalizedSubscription.next_billing_date,
              end_date: normalizedSubscription.end_date,
              updated_at: new Date().toISOString(),
            },
            true
          );
          statusLogger.info('[ASSINATURA] Assinatura local atualizada para status cancelada após expiração');
        } catch (updateError) {
          statusLogger.warn('[ASSINATURA] Falha ao persistir downgrade da assinatura local expirada:', updateError);
        }
      }

      statusLogger.info(`[ASSINATURA] Verificação de data - Agora: ${now}, Data de expiração: ${endDate?.getTime() || 'indefinida'}`);
      statusLogger.info(`[ASSINATURA] Resultado da verificação de atividade: ${isActive ? 'ATIVA' : 'INATIVA'}`);
      statusLogger.info(`[ASSINATURA] Retornando assinatura local encontrada: ${subscription.id}`);
      statusLogger.info(`[DEBUG-PAUSE] checkSubscriptionStatus - Usando assinatura local para atualizar estado global.`);

      return mapDbSubscriptionToContract(normalizedSubscription, plan);
    }
    if (options.allowRemote === false) {
      statusLogger.info('[ASSINATURA] Consulta remota bloqueada; usando fallback local free.');
      return buildFreeSubscription(empresaId);
    }

    // Se não encontrar local, verificar no Supabase    
    remoteDbLogger.info(`Assinatura não encontrada localmente. Verificando no Supabase...`);

    const { data: remoteSubscriptions, error } = await supabase
      .from(APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME)
      .select('*, subscription_plan:subscription_plan_id(*)')
      .eq('empresa_id', empresaId)
      .in('status', ['ativa', 'cancelamento_agendado'])
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      remoteDbLogger.error('Erro ao verificar assinatura no Supabase:', error);
      return buildFreeSubscription(empresaId);
    }
    
    remoteDbLogger.info(`Assinaturas remotas encontradas: ${remoteSubscriptions?.length || 0}`,
      remoteSubscriptions?.length > 0 ? JSON.stringify(remoteSubscriptions[0]) : 'nenhuma');

    // Se encontrar assinatura ativa remota
    if (remoteSubscriptions && remoteSubscriptions.length > 0) {
      const subscription = remoteSubscriptions[0] as any;
      statusLogger.info(`[ASSINATURA] Assinatura ativa encontrada no Supabase: ${subscription.id}`);
      statusLogger.info(`[DEBUG-PAUSE] checkSubscriptionStatus - next_billing_date (remoto): ${JSON.stringify(subscription.next_billing_date)}`);

      const { subscription: normalizedSubscription, wasAdjusted, reason } = normalizeSubscriptionForExpiration(subscription);
      if (wasAdjusted) {
        statusLogger.info(`[ASSINATURA] Assinatura remota indica expiração (${reason}). Ajustando dados locais para plano free.`);
      }

      const plan = normalizedSubscription.subscription_plan;
      const now = new Date().getTime();
      const endDate = normalizedSubscription.end_date ? new Date(normalizedSubscription.end_date) : null;
      const nextBillingDate = normalizedSubscription.next_billing_date;
      const isActive = (normalizedSubscription.status === 'ativa' || normalizedSubscription.status === 'cancelamento_agendado')
        && (!endDate || endDate.getTime() > now);

      statusLogger.info(`[ASSINATURA] Verificação de data remota - Agora: ${now}, Data de expiração: ${endDate?.getTime() || 'indefinida'}`);
      statusLogger.info(`[ASSINATURA] Resultado da verificação de atividade remota: ${isActive ? 'ATIVA' : 'INATIVA'}`);
      statusLogger.info(`[ASSINATURA] Retornando assinatura remota encontrada: ${normalizedSubscription.id}`);

      // Salvar no banco local se não existir ainda
      try {
        // Separar o objeto aninhado do plano antes de salvar a assinatura
        const planDetails = normalizedSubscription.subscription_plan;
        const subscriptionToSave = { ...normalizedSubscription };
        delete subscriptionToSave.subscription_plan;

        localDbLogger.info(`Salvando assinatura no banco local: ${subscription.id}`);
        const { id: subscriptionId, ...subscriptionData } = subscriptionToSave;
        const updated = await databaseService.updateItem(
          APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME,
          subscriptionId,
          subscriptionData,
          { skipPendingOperation: true, source: 'remote' }
        );

        if (!updated) {
          await databaseService.createItem(
            APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME,
            subscriptionToSave,
            { skipPendingOperation: true, source: 'remote' }
          );
          statusLogger.info(`[ASSINATURA] Assinatura salva no banco local com sucesso`);
        } else {
          statusLogger.info(`[ASSINATURA] Assinatura local existente atualizada com dados remotos normalizados`);
        }
      } catch (saveError) {
        statusLogger.warn('[ASSINATURA] Erro ao salvar assinatura no banco local:', saveError);
        localDbLogger.warn('Erro ao salvar assinatura no banco local:', saveError);
      }
      statusLogger.info(`[DEBUG-PAUSE] checkSubscriptionStatus - Usando assinatura remota para atualizar estado global.`);

      return mapDbSubscriptionToContract(normalizedSubscription, plan);
    }
    // Se não encontrar nenhuma assinatura ativa
    statusLogger.info(`[ASSINATURA] Nenhuma assinatura ativa encontrada para empresa ${empresaId}`);
    return buildFreeSubscription(empresaId);
  } catch (error) {
    statusLogger.error('[ASSINATURA] Exceção ao verificar status da assinatura:', error);
    return buildFreeSubscription(empresaId);
  }
};

/**
 * Obtém assinaturas disponíveis para compra
 */
export const getAvailablePlans = async () => {
  plansLogger.info('Buscando planos disponíveis');

  try {
    // Buscar no banco local para fallback offline, mas manter Supabase como fonte fresca.
    localDbLogger.info('Buscando planos no banco local');
    const localPlans = await databaseService.listItems(
      APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTION_PLANS.NAME,
      {}
    );

    const hasLocalPlans = Boolean(localPlans && localPlans.length > 0);
    if (hasLocalPlans) {
      plansLogger.info(`Encontrados ${localPlans.length} planos no banco local`);
    }

    // Buscar no Supabase para refletir novos preços e planos.
    remoteDbLogger.info('Buscando planos no Supabase...');
    const { data, error } = await supabase
      .from(APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTION_PLANS.NAME)
      .select('*')
      .order('price');

    if (error) {
      remoteDbLogger.error('Erro ao buscar planos no Supabase:', error);
      if (hasLocalPlans) {
        plansLogger.warn('Usando planos locais por falha ao atualizar planos no Supabase');
        return { data: localPlans, error: null };
      }
      return { data: [], error };
    }

    // Salvar planos no banco local para uso offline
    if (data && data.length > 0) {
      //plansLogger.info(`Salvando ${data.length} planos no banco local`);
      for (const plan of data) {
        try {
          const existingPlan = localPlans?.find((localPlan: any) => localPlan.id === plan.id);
          if (existingPlan) {
            const { id, ...planUpdates } = plan;
            localDbLogger.info(`Atualizando plano ${plan.id} no banco local`);
            await databaseService.updateItem(
              APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTION_PLANS.NAME,
              id,
              planUpdates,
              { skipPendingOperation: true, source: 'remote' }
            );
          } else {
            localDbLogger.info(`Salvando plano ${plan.id} no banco local`);
            await databaseService.createItem(
              APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTION_PLANS.NAME,
              plan,
              { skipPendingOperation: true, source: 'remote' }
            );
          }
        } catch (saveError) {
          localDbLogger.warn(`Erro ao salvar plano ${plan.id} no banco local:`, saveError);
        }
      }
    }

    return { data: data || [], error: null };
  } catch (error) {
    plansLogger.error('Exceção ao buscar planos disponíveis:', error);
    return { data: [], error };
  }
};

/**
 * Obtém os pagamentos associados a uma empresa
 */
export const getUserPayments = async (empresaId: string) => {
  paymentsLogger.info(`Buscando pagamentos para empresa: ${empresaId}`);

  try {
    // Buscar no banco local primeiro
    localDbLogger.info(`Buscando pagamentos no banco local para empresa: ${empresaId}`);
    const localPayments = await databaseService.listItems(
      APP_CONSTANTS.DATABASE.TABLES.PAYMENTS.NAME,
      { empresa_id: empresaId }
    );

    if (localPayments && localPayments.length > 0) {
      paymentsLogger.info(`Encontrados ${localPayments.length} pagamentos no banco local`);
    }

    // Buscar dados mais recentes no Supabase
    remoteDbLogger.info(`Buscando pagamentos no Supabase para empresa: ${empresaId}`);
    const supabasePageSize = APP_CONSTANTS.SYNC?.SUPABASE_PULL_PAGE_SIZE ?? 500;
    const buildBaseQuery = () =>
      supabase
        .from(APP_CONSTANTS.DATABASE.TABLES.PAYMENTS.NAME)
        .select('*')
        .eq('empresa_id', empresaId)
        .order('payment_date', { ascending: false });

    const remotePayments: any[] = [];
    let pageIndex = 0;
    let remoteError: any = null;

    while (true) {
      const from = pageIndex * supabasePageSize;
      const to = from + supabasePageSize - 1;

      const { data, error } = await buildBaseQuery().range(from, to);

      if (error) {
        remoteError = error;
        remoteDbLogger.error(`Erro ao buscar pagamentos no Supabase (página ${pageIndex}):`, error);
        break;
      }

      const pageData = data ?? [];

      if (pageIndex === 0 && pageData.length === 0) {
        remoteDbLogger.info('Nenhum pagamento encontrado no Supabase');
        break;
      }

      if (pageData.length === 0) {
        break;
      }

      remotePayments.push(...pageData);

      if (pageData.length < supabasePageSize) {
        break;
      }

      pageIndex++;
    }

    if (remoteError) {
      // Retorna dados locais (se houver) e o erro remoto
      return { data: localPayments || [], error: remoteError };
    }

    paymentsLogger.info(`Encontrados ${remotePayments.length} pagamentos no Supabase`);

    // Atualizar o banco local com o resultado remoto para uso offline
    for (const payment of remotePayments) {
      const { id, ...paymentData } = payment;
      if (!id) {
        localDbLogger.warn('Pagamento recebido sem ID ao sincronizar dados locais. Ignorando registro.');
        continue;
      }
      try {
        const updated = await databaseService.updateItem(
          APP_CONSTANTS.DATABASE.TABLES.PAYMENTS.NAME,
          id,
          paymentData,
          { skipPendingOperation: true, source: 'remote' }
        );

        if (!updated) {
          await databaseService.createItem(
            APP_CONSTANTS.DATABASE.TABLES.PAYMENTS.NAME,
            payment,
            { skipPendingOperation: true, source: 'remote' }
          );
        }
      } catch (syncError) {
        localDbLogger.warn('Falha ao sincronizar pagamento no banco local:', syncError);
      }
    }

    // Caso não existam dados remotos, usa os locais (se houver)
    if (remotePayments.length === 0 && localPayments && localPayments.length > 0) {
      return { data: localPayments, error: null };
    }

    return { data: remotePayments, error: null };
  } catch (error) {
    paymentsLogger.error('Exceção ao buscar pagamentos da empresa:', error);
    return { data: [], error };
  }
};

/**
 * Inicia processo de assinatura de um plano (placeholder)
 */
export const subscribeToPlan = async (planId: string, userId: string, empresaId: string) => {
  subscribeLogger.info(`Iniciando processo de assinatura para plano: ${planId}, usuário: ${userId}, empresa: ${empresaId}`);

  // Aqui seria implementada a integração com gateway de pagamento
  // Este é apenas um placeholder
  subscribeLogger.info('Função de assinatura em modo placeholder (não implementada)');

  return {
    success: true,
    message: 'Função de assinatura ainda não implementada. Este é apenas um placeholder.',
    checkoutUrl: null
  };
};

/**
 * Inicia um processo de assinatura utilizando o Stripe
 * @param priceId ID do preço no Stripe
 * @param couponId ID do cupom de desconto (opcional)
 * @returns Um objeto contendo o ID da sessão do Stripe
 * @throws Error se não estiver no ambiente web ou se falhar ao criar a sessão
 */
export const subscribeWithStripe = async (priceId: string, couponId?: string) => {
  subscribeLogger.info(`Iniciando assinatura com Stripe - Preço: ${priceId}, Cupom: ${couponId || 'nenhum'}`);

  if (!isWeb) {
    subscribeLogger.warn('Tentativa de iniciar assinatura com Stripe fora do ambiente web');
    throw new Error("consumption-only");
  }

  try {
    // 1) Buscar dados de empresa e perfil do estado global
    let companyData = globalState.company.data.get();
    const userProfile = globalState.user.profile.get();
    const empresaId = companyData?.id || userProfile?.empresa_id;
    let customerId = companyData?.stripe_customer_id;
    const email = userProfile?.email;

    subscribeLogger.info(`Dados para assinatura - Empresa: ${empresaId}, Customer: ${customerId || 'não existe'}, Email: ${email || 'não definido'}`);

    if (!empresaId) {
      throw new Error("Empresa não encontrada");
    }

    if (!companyData || companyData.id !== empresaId) {
      subscribeLogger.info(`Empresa ausente no estado global. Carregando empresa ${empresaId} antes do checkout...`);
      const { data: loadedCompany, error: companyError } = await getUserCompany(empresaId);
      if (companyError || !loadedCompany) {
        subscribeLogger.error('Erro ao carregar empresa antes do checkout:', companyError);
        throw new Error("Empresa não encontrada");
      }
      companyData = loadedCompany;
      customerId = companyData?.stripe_customer_id;
    }

    // 2) Se não existir stripe_customer_id, criar no Stripe usando a função já existente
    if (!customerId) {
      subscribeLogger.info('Stripe Customer ID não encontrado. Criando novo Customer no Stripe...');

      try {
        // Usar a função createCustomer já implementada no serviço
        const customerData = await createCustomer(email || '', { empresaId });
        customerId = customerData.customerId;

        subscribeLogger.info(`Novo Stripe Customer criado com sucesso: ${customerId}`);
        // Nota: o createCustomer já atualiza o estado global com o novo customerId
      } catch (error: any) {
        subscribeLogger.error('Erro ao criar Stripe Customer:', error);
        throw new Error(`Falha ao criar Stripe Customer: ${error.message || 'Erro desconhecido'}`);
      }
    }

    // 3) Chamar a Edge Function para criar a sessão de checkout
    const createCheckoutFn = getStripeEdgeFunctionName('createCheckout');
    subscribeLogger.info(`Chamando Edge Function ${createCheckoutFn}`);

    // Set a timeout to cancel the request if it takes too long
    const timeoutMs = 15000; // 15 seconds
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("Tempo de conexão excedido. Tente novamente mais tarde.")), timeoutMs);
    });

    // Combine the timeout with the actual request
    const response = await Promise.race([
      supabase.functions.invoke(createCheckoutFn, {
        body: { priceId, couponId, empresaId }
      }),
      timeoutPromise
    ]);

    // Check for errors - typescript won't allow type checking here due to Promise.race
    const { data, error } = response as any;

    if (error) {
      subscribeLogger.error('Erro ao criar sessão de checkout:', error);
      const errorMessage = error.message || "Erro ao criar sessão de pagamento";
      if (errorMessage.includes("CORS") || errorMessage.includes("Network Error")) {
        throw new Error(`Erro de conexão ao servidor. Verifique sua conexão com a internet ou tente novamente mais tarde.`);
      }
      throw new Error(`Falha ao criar sessão: ${errorMessage}`);
    }

    if (!data || !data.sessionUrl) {
      throw new Error("Resposta do servidor inválida. Tente novamente.");
    }

    const { sessionId, sessionUrl } = data;
    subscribeLogger.info(`Sessão Stripe criada com sucesso: ${sessionId} e url: ${sessionUrl}`);

    // Redirecionar para o Stripe Checkout
    window.location.href = sessionUrl;

    return { sessionId, sessionUrl };
  } catch (error: any) {
    subscribeLogger.error('Erro ao iniciar assinatura com Stripe:', error);
    if (error.code === 'NETWORK_ERROR' || error.message?.includes('Failed to fetch')) {
      throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
    }
    throw error;
  }
};

const applyApplePurchaseResult = (empresaId: string, result: AppleIapPurchaseResult) => {
  const plan = APP_CONSTANTS.APPLE_IAP.PLANS.find(
    (entry) => entry.productId === result.productId,
  );
  if (!plan || result.legacyStatus === 'cancelada') return;

  userActions.setSubscriptionStatus({
    id: result.subscriptionId,
    empresa_id: empresaId,
    externalId: result.externalSubscriptionId,
    status: result.legacyStatus,
    planType: 'premium',
    planName: `Premium ${result.tier} fotos`,
    planId: plan.subscriptionPlanId,
    startDate: new Date().toISOString(),
    nextBillingDate: result.expiresAt,
    expiration: result.expiresAt,
    lastChecked: new Date().toISOString(),
    billingProvider: 'apple',
    isManual: false,
    paymentMethod: 'apple_iap',
  });

  const company = globalState.company.data.get();
  if (company?.id === empresaId) {
    companyActions.setCompanyData({ ...company, plano: 'premium', num_fotos: result.tier });
  }
};

export const subscribeWithApple = async (productId: string, empresaId: string) => {
  mainLogger.info('[APPLE-IAP] Iniciando assinatura pela App Store');
  const result = await subscribeWithAppleIap(productId, empresaId);
  applyApplePurchaseResult(empresaId, result);
  return result;
};

export const restoreApplePurchases = async (empresaId: string) => {
  mainLogger.info('[APPLE-IAP] Iniciando restauracao de compras da App Store');
  const results = await restoreAppleIapPurchases(empresaId);
  const active = results.find((result) => result.legacyStatus !== 'cancelada');
  if (active) applyApplePurchaseResult(empresaId, active);
  return results;
};

/**
 * Confirma o sucesso da sessão Stripe após o retorno do checkout
 * @param sessionId ID da sessão do Stripe
 * @returns Dados da assinatura confirmada
 */
export const handleSuccess = async (sessionId: string) => {
  try {
    const handleSuccessFn = getStripeEdgeFunctionName('handleSuccess');
    subscribeLogger.info(`Chamando Edge Function ${handleSuccessFn}`);
    const { data, error } = await supabase.functions.invoke(handleSuccessFn, {
      body: { session_id: sessionId }
    });

    if (error) throw new Error(error.message || 'Falha ao confirmar sessão');

    // Normalização dos campos que podem vir em snake_case da API
    const normalizedData = {
      status: data.status,
      subscriptionId: data.subscription_id || data.subscriptionId,
      nextBillingDate: data.next_billing_date || data.nextBillingDate
    };

    // Atualizar o estado global com status de assinatura premium
    if (normalizedData.status === 'paid') {
      userActions.setSubscriptionStatus({
        status: 'ativa',
        planType: 'premium',
        externalId: normalizedData.subscriptionId ?? '',
        nextBillingDate: normalizedData.nextBillingDate ? new Date(normalizedData.nextBillingDate).toISOString() : null,
        lastChecked: new Date().toISOString(),
        billingProvider: 'stripe',
        isManual: false,
        paymentMethod: 'stripe',
      });

      // Se houver um perfil de empresa, atualizar o plano
      const companyData = globalState.company.data.get();
      if (companyData) {
        companyActions.setCompanyData({
          ...companyData,
          plano: 'premium'
        });
      }
    }

    return normalizedData;
  } catch (err) {
    throw err;
  }
};

/**
 * Obtém os detalhes de uma assinatura existente
 * @param externalSubscriptionId ID externo da assinatura no Stripe (opcional se houver dados no estado global)
 * @param forceRefresh Se true, força uma nova busca mesmo que exista cache
 * @returns Dados da assinatura
 */
export const getSubscription = async (externalSubscriptionId?: string, forceRefresh = false) => {
  try {
    remoteDbLogger.info('[getSubscription] Iniciando processo', { externalSubscriptionId, forceRefresh });
    let external_subscription_id: string | null = externalSubscriptionId ?? null;
    let customer_id: string | null = null;
    let empresaIdFromState: string | null = null;

    const subscriptionSnapshot = globalState.subscription.get();
    const companyData = globalState.company.data.get();

    if (!external_subscription_id) {
      remoteDbLogger.info('[getSubscription] Buscando dados do estado global');
      if (subscriptionSnapshot.externalId && isManualExternalId(subscriptionSnapshot.externalId)) {
        external_subscription_id = subscriptionSnapshot.externalId;
        empresaIdFromState = companyData?.id ?? null;
        remoteDbLogger.info('[getSubscription] Usando externalId manual do estado global', { external_subscription_id, empresaIdFromState });
      } else if (subscriptionSnapshot.externalId) {
        external_subscription_id = subscriptionSnapshot.externalId;
        remoteDbLogger.info('[getSubscription] Usando externalId do estado global', { external_subscription_id });
      } else if ((subscriptionSnapshot.status === 'ativa' || subscriptionSnapshot.status === 'cancelamento_agendado') && companyData?.stripe_customer_id) {
        customer_id = companyData.stripe_customer_id;
        remoteDbLogger.info('[getSubscription] Usando stripe_customer_id do estado global', { customer_id });
      } else if (companyData?.stripe_customer_id) {
        customer_id = companyData.stripe_customer_id;
        remoteDbLogger.info('[getSubscription] Usando stripe_customer_id da empresa', { customer_id });
      } else {
        remoteDbLogger.info('[getSubscription] Não encontrou referências suficientes para buscar assinatura');
        throw new Error('ID de assinatura ou customer não disponível no estado global');
      }
    }

    const useCachedValue = !forceRefresh &&
      subscriptionSnapshot.lastChecked &&
      (new Date().getTime() - new Date(subscriptionSnapshot.lastChecked).getTime() < 5 * 60 * 1000);

    if (useCachedValue) {
      remoteDbLogger.info('[getSubscription] Retornando assinatura do cache em memória', { lastChecked: subscriptionSnapshot.lastChecked });
      return subscriptionSnapshot;
    }

    if (external_subscription_id && isManualExternalId(external_subscription_id)) {
      const billingProvider = resolveBillingProvider(external_subscription_id);
      remoteDbLogger.info('[getSubscription] Processando assinatura sem consulta ao Stripe', { external_subscription_id, billingProvider });
      const empresaId = empresaIdFromState ?? subscriptionSnapshot.empresa_id ?? companyData?.id ?? null;
      if (!empresaId) {
        throw new Error('Empresa não encontrada para assinatura manual');
      }

      let manualSubscription: any | null = null;
      const localMatches = await getLocalSubscriptionByExternalId(external_subscription_id, empresaId);
      if (localMatches && localMatches.length > 0) {
        manualSubscription = localMatches[0];
        remoteDbLogger.info('[getSubscription] Assinatura manual encontrada no banco local', { id: manualSubscription.id });
      }

      if (!manualSubscription) {
        const { data: remoteManual, error: remoteManualErr } = await supabase
          .from(APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME)
          .select('*, subscription_plan:subscription_plan_id(*)')
          .eq('empresa_id', empresaId)
          .eq('external_subscription_id', external_subscription_id)
          .single();

        if (remoteManualErr) {
          remoteDbLogger.error('[getSubscription] Falha ao buscar assinatura manual no Supabase', remoteManualErr);
          throw new Error(remoteManualErr.message || 'Assinatura manual não encontrada no Supabase');
        }

        manualSubscription = remoteManual;
        remoteDbLogger.info('[getSubscription] Assinatura manual carregada do Supabase', { id: manualSubscription.id });
      }

      const planDetails = manualSubscription.subscription_plan;
      if (manualSubscription.subscription_plan) {
        delete manualSubscription.subscription_plan;
      }

      const contract = mapDbSubscriptionToContract(manualSubscription, planDetails);
      userActions.setSubscriptionStatus(contract);

      try {
        const { id: localId, ...subscriptionData } = manualSubscription;
        const updated = await databaseService.updateItem(
          APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME,
          localId,
          subscriptionData,
          { skipPendingOperation: true, source: 'remote' }
        );

        if (!updated) {
          await databaseService.createItem(
            APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME,
            manualSubscription,
            { skipPendingOperation: true, source: 'remote' }
          );
        }
      } catch (saveError) {
        localDbLogger.warn('[getSubscription] Erro ao salvar assinatura manual no banco local', saveError);
      }

      return {
        id: contract.id,
        externalId: contract.externalId,
        status: contract.status,
        currentPeriodEnd: contract.nextBillingDate,
        startDate: contract.startDate,
        nextBillingDate: contract.nextBillingDate,
        end_date: contract.expiration,
        billingProvider: contract.billingProvider,
        paymentMethod: contract.paymentMethod,
      };
    }

    const getSubscriptionFn = getStripeEdgeFunctionName('getSubscription');
    remoteDbLogger.info(`[getSubscription] Chamando função remota ${getSubscriptionFn}`, { external_subscription_id, customer_id });
    const { data, error } = await supabase.functions.invoke(getSubscriptionFn, {
      body: {
        external_subscription_id,
        customer_id
      }
    });

    if (error) {
      remoteDbLogger.info('[getSubscription] Erro retornado pela função remota', { error });
      throw new Error(error.message || 'Falha ao buscar assinatura');
    }

    if (!data || typeof data !== 'object') {
      remoteDbLogger.info('[getSubscription] Resposta inválida da API de assinatura', { data });
      const fallbackInfo = buildFreeSubscription(subscriptionSnapshot.empresa_id || companyData?.id || '');
      userActions.setSubscriptionStatus(fallbackInfo);
      return {
        id: external_subscription_id || customer_id || 'unknown',
        status: 'free',
        currentPeriodEnd: null,
        startDate: null,
        nextBillingDate: null,
        externalId: fallbackInfo.externalId,
        billingProvider: fallbackInfo.billingProvider,
        paymentMethod: fallbackInfo.paymentMethod,
      };
    }

    const rawStatus = mapSubscriptionStatus(data.status, data.cancel_at_period_end);
    const allowedStatuses: SubscriptionInfo['status'][] = ['ativa', 'cancelamento_agendado', 'cancelada', 'free'];
    const normalizedStatus = allowedStatuses.includes(rawStatus as SubscriptionInfo['status'])
      ? (rawStatus as SubscriptionInfo['status'])
      : 'free';

    const normalizedData = {
      id: data.id || external_subscription_id || customer_id || 'unknown',
      status: normalizedStatus,
      currentPeriodEnd: data.current_period_end || data.currentPeriodEnd || null,
      startDate: data.start_date || data.startDate || null,
      nextBillingDate: data.next_billing_date || data.nextBillingDate || null,
      end_date: data.current_period_end || data.currentPeriodEnd || null,
      externalId: data.id || external_subscription_id || '',
      billingProvider: 'stripe' as SubscriptionBillingProvider,
      paymentMethod: 'stripe' as const,
    };

    remoteDbLogger.info('[getSubscription] Dados normalizados recebidos da API', { normalizedData });
    userActions.setSubscriptionStatus({
      externalId: normalizedData.externalId,
      status: normalizedData.status,
      nextBillingDate: normalizedData.nextBillingDate,
      expiration: normalizedData.end_date,
      lastChecked: new Date().toISOString(),
      billingProvider: 'stripe',
      isManual: false,
      paymentMethod: 'stripe',
    });

    return normalizedData;
  } catch (err) {
    remoteDbLogger.error('[getSubscription] Erro ao buscar detalhes da assinatura', err);
    const fallbackData = {
      id: externalSubscriptionId || 'unknown',
      status: 'error',
      currentPeriodEnd: null,
      startDate: null,
      nextBillingDate: null,
      error: err instanceof Error ? err.message : 'Erro desconhecido',
    };
    remoteDbLogger.info('[getSubscription] Retornando fallbackData após erro', { fallbackData });
    return fallbackData;
  }
};

/**
 * Mapeia os status de assinatura retornados pela API para os valores usados no app
 * @param apiStatus Status retornado pela API
 * @returns Status normalizado para uso no app
 */
const mapSubscriptionStatus = (apiStatus: string | undefined, cancelAtPeriodEnd: boolean | undefined): string => {
  if (!apiStatus) return 'unknown';

  if (cancelAtPeriodEnd) {
    return 'cancelamento_agendado';
  }

  switch (apiStatus.toLowerCase()) {
    case 'active':
      return 'ativa';
    case 'canceled':
    case 'cancelled':
      return 'cancelada';
    case 'past_due':
      return 'atrasado';
    case 'unpaid':
      return 'inativa';
    case 'trialing':
      return 'ativa';
    case 'incomplete':
    case 'incomplete_expired':
      return 'falha';
    default:
      return apiStatus;
  }
};

/**
 * Cancela uma assinatura existente
 * @param externalSubscriptionId ID externo da assinatura no Stripe
 * @returns Dados da operação de cancelamento
 */
export const cancelSubscription = async (externalSubscriptionId: string) => {
  try {
    mainLogger.info(`Iniciando cancelamento da assinatura com external_id: ${externalSubscriptionId}`);

    if (isAppleExternalId(externalSubscriptionId)) {
      mainLogger.warn('Cancelamento Apple solicitado via fluxo Stripe');
      throw new Error('Assinaturas da App Store devem ser gerenciadas pela Apple.');
    }

    if (isManualExternalId(externalSubscriptionId)) {
      mainLogger.warn('Cancelamento manual solicitado via fluxo Stripe');
      throw new Error('Assinaturas manuais devem ser gerenciadas pelo painel do desenvolvedor.');
    }

    // 1. Chamar a Edge Function para realizar cancelamento no Stripe
    const cancelSubscriptionFn = getStripeEdgeFunctionName('cancelSubscription');
    mainLogger.info(`Usando Edge Function ${cancelSubscriptionFn} para cancelar`);
    const { data, error } = await supabase.functions.invoke(cancelSubscriptionFn, {
      body: { external_subscription_id: externalSubscriptionId }
    });

    if (error) throw new Error(error.message || 'Falha ao cancelar assinatura');

    // 2. Destruturação direta dos valores retornados
    const { subscription, end_date, updated_at } = data;

    // 2. Obter dados da empresa atual
    const companyData = globalState.company.data.get();
    const empresaId = companyData?.id;

    if (!empresaId) {
      mainLogger.warn('Empresa não encontrada ao cancelar assinatura');
      throw new Error('Empresa não encontrada');
    }

    // 3. Buscar a assinatura local pelo external_subscription_id usando nossa função auxiliar
    const localSubscriptions = await getLocalSubscriptionByExternalId(externalSubscriptionId, empresaId);

    // 4. Se encontrou a assinatura local, atualizá-la usando o ID LOCAL (PK)
    if (localSubscriptions && localSubscriptions.length > 0) {
      const localSubscription = localSubscriptions[0];

      localDbLogger.info(`Atualizando assinatura local (id: ${localSubscription.id}) para status 'cancelamento_agendado'`);

      // Usar o ID LOCAL da assinatura (não o external_id) para a atualização no banco
      const localCancellationUpdate: Record<string, unknown> = {
        status: 'cancelamento_agendado',
        end_date: end_date || null,
      };
      if (updated_at !== undefined && updated_at !== null) {
        localCancellationUpdate.updated_at = updated_at;
      }
      await databaseService.updateItem(
        APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME,
        localSubscription.id, // ID LOCAL - chave primária
        localCancellationUpdate,
        { skipPendingOperation: true, source: 'remote' }
      );

      localDbLogger.info(`Assinatura local (id: ${localSubscription.id}, external_id: ${externalSubscriptionId}) atualizada com sucesso para cancelamento_agendado`);
    } else {
      localDbLogger.warn(`Não foi encontrada assinatura local para atualizar. Empresa: ${empresaId}, External ID: ${externalSubscriptionId}`);
    }

    // 5. Atualizar o estado global - mantém isActive=true até o end_date
    const endDate = end_date ? new Date(end_date) : null;

    userActions.setSubscriptionStatus({
      status: 'cancelamento_agendado',
      planType: 'premium',
      expiration: endDate ? endDate.toISOString() : null,
      lastChecked: new Date().toISOString(),
      billingProvider: 'stripe',
      isManual: false,
      paymentMethod: 'stripe',
    });

    mainLogger.info(`Cancelamento processado com sucesso. Acesso até: ${endDate?.toISOString() || 'indefinido'}`);

    return data;
  } catch (err) {
    mainLogger.error('Erro ao processar cancelamento:', err);
    throw err;
  }
};

/**
 * Cria um customer no Stripe e atualiza o guincho_empresas
 * @param email Email do cliente
 * @param metadata Metadados adicionais (opcional)
 * @returns ID do customer criado
 */
export const createCustomer = async (email: string, metadata: any = {}) => {
  const companyData = globalState.company.data.get();
  // Use o companyId passado via metadata, se existir, senão usa o do estado global
  const empresaId = metadata?.empresaId || companyData?.id;

  if (!empresaId) {
    throw new Error('Empresa não encontrada. Impossível criar customer.');
  } else { mainLogger.info(`createCustomer - empresaId: ${empresaId}`); }

  try {
    // Set a timeout to cancel the request if it takes too long
    const timeoutMs = 15000; // 15 seconds
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("Tempo de conexão excedido. Tente novamente mais tarde.")), timeoutMs);
    });

    // Combine the timeout with the actual request
    const response = await Promise.race([
      supabase.functions.invoke('createCustomer', {
        body: { email, metadata: { ...metadata, empresaId }, empresaId }
      }),
      timeoutPromise
    ]);

    // Check for errors - typescript won't allow type checking here due to Promise.race
    const { data, error } = response as any;

    if (error) {
      const errorMessage = error.message || "Erro ao criar customer";
      if (errorMessage.includes("CORS") || errorMessage.includes("Network Error")) {
        throw new Error(`Erro de conexão ao servidor. Verifique sua conexão com a internet ou tente novamente mais tarde.`);
      }
      throw new Error(errorMessage || 'Falha ao criar Customer');
    }

    if (!data || !data.customerId) {
      throw new Error("Resposta do servidor inválida. Tente novamente.");
    }

    // Atualizar o estado global com o novo customerId e garantir que o id da empresa está correto
    if (companyData) {
      const updatedCompany = {
        ...companyData,
        id: empresaId, // garantir que o id está atualizado
        stripe_customer_id: data.customerId
      };
      companyActions.setCompanyData(updatedCompany);
    }

    return data;
  } catch (error: any) {
    if (error.code === 'NETWORK_ERROR' || error.message?.includes('Failed to fetch')) {
      throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
    }
    throw error;
  }
};

/**
 * Busca os preços disponíveis no Stripe
 * @returns Lista de preços ativos
 */
export const listPrices = async () => {
  try {
    const listPricesFn = getStripeEdgeFunctionName('listPrices');
    plansLogger.info(`Chamando Edge Function ${listPricesFn} para listar preços`);
    const { data, error } = await supabase.functions.invoke(listPricesFn);
    if (error) throw new Error(error.message || 'Falha ao listar preços');
    return data;
  } catch (err) {
    throw err;
  }
};

export const generateManualExternalId = (): string => `${MANUAL_EXTERNAL_ID_PREFIX}${uuidv4()}`;

export const upsertSubscriptionAdmin = async (input: AdminSubscriptionInput) => {
  const {
    empresaId,
    subscriptionPlanId,
    status = 'ativa',
    startDate,
    nextBillingDate,
    endDate,
    externalSubscriptionId,
  } = input;

  const { data, error } = await supabase.functions.invoke('manualSubscription', {
    body: {
      action: 'upsert_subscription',
      payload: {
        empresaId,
        subscriptionPlanId,
        status,
        startDate,
        nextBillingDate,
        endDate,
        externalSubscriptionId,
      },
    },
  });

  if (error) {
    mainLogger.error('[MANUAL] Falha ao salvar assinatura via função manualSubscription', error);
    throw new Error(error.message || 'Falha ao salvar assinatura manual');
  }

  const response = data as any;
  const manualRecord = response?.subscription;
  const contract = response?.contract;

  if (!manualRecord || !contract) {
    throw new Error('Resposta inválida da função manualSubscription ao salvar assinatura.');
  }

  if (manualRecord.subscription_plan) {
    delete manualRecord.subscription_plan;
  }

  try {
    const { id: localId, ...subscriptionData } = manualRecord;
    const updated = await databaseService.updateItem(
      APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME,
      localId,
      subscriptionData,
      { skipPendingOperation: true, source: 'remote' }
    );

    if (!updated) {
      await databaseService.createItem(
        APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME,
        manualRecord,
        { skipPendingOperation: true, source: 'remote' }
      );
    }
  } catch (err) {
    localDbLogger.warn('[MANUAL] Erro ao sincronizar assinatura manual no banco local', err);
  }

  userActions.setSubscriptionStatus(contract);
  const currentCompany = globalState.company.data.get();
  if (currentCompany?.id === empresaId) {
    companyActions.setCompanyData({
      ...currentCompany,
      plano: contract.planType,
    });
  }

  return {
    subscription: manualRecord,
    contract,
  };
};

interface TrialActivationOptions {
  empresaId: string;
  subscriptionPlanId?: string;
  durationDays?: number;
  externalSubscriptionId?: string;
  startDate?: string | Date;
}

export const activateTrialForCompany = async ({
  empresaId,
  subscriptionPlanId,
  durationDays,
  externalSubscriptionId,
  startDate,
}: TrialActivationOptions) => {
  if (!empresaId) {
    throw new Error('empresaId é obrigatório para ativar o trial.');
  }

  const trialPlanId =
    subscriptionPlanId ||
    APP_CONSTANTS.SUBSCRIPTION_TRIAL.PLAN_ID;

  if (!trialPlanId) {
    throw new Error('ID do plano de trial não configurado. Defina EXPO_PUBLIC_SUBSCRIPTION_TRIAL_PLAN_ID ou APP_CONSTANTS.SUBSCRIPTION_TRIAL.PLAN_ID.');
  }

  const trialDays =
    durationDays ??
    APP_CONSTANTS.SUBSCRIPTION_TRIAL.DURATION_DAYS ??
    3;

  const now = startDate ? new Date(startDate) : new Date();
  if (Number.isNaN(now.getTime())) {
    throw new Error('Data inicial inválida ao tentar criar o trial.');
  }

  const trialEnd = new Date(now.getTime() + trialDays * 24 * 60 * 60 * 1000);
  const externalId =
    externalSubscriptionId ||
    `${APP_CONSTANTS.SUBSCRIPTION_TRIAL.EXTERNAL_ID_PREFIX || 'manual:trial-'}${empresaId}`;

  mainLogger.info('[TRIAL] Ativando período de testes', {
    empresaId,
    trialPlanId,
    trialEnd: trialEnd.toISOString(),
  });

  const result = await upsertSubscriptionAdmin({
    empresaId,
    subscriptionPlanId: trialPlanId,
    status: 'ativa',
    startDate: now.toISOString(),
    endDate: trialEnd.toISOString(),
    externalSubscriptionId: externalId,
  });

  return {
    ...result,
    trialEndsAt: trialEnd.toISOString(),
  };
};

export const registerManualPayment = async (input: AdminPaymentInput) => {
  const {
    empresaId,
    subscriptionExternalId,
    amount,
    paymentDate,
    status = 'paid',
    reference,
  } = input;

  if (!empresaId) throw new Error('empresaId é obrigatório para registrar pagamento manual');
  if (!subscriptionExternalId) throw new Error('subscriptionExternalId é obrigatório para registrar pagamento manual');
  if (Number.isNaN(amount) || amount <= 0) throw new Error('amount inválido para pagamento manual');

  const { data, error } = await supabase.functions.invoke('manualSubscription', {
    body: {
      action: 'register_payment',
      payload: {
        empresaId,
        subscriptionExternalId,
        amount,
        paymentDate,
        status,
        reference,
      },
    },
  });

  if (error) {
    paymentsLogger.error('[MANUAL] Falha ao registrar pagamento via função manualSubscription', error);
    throw new Error(error.message || 'Falha ao registrar pagamento manual');
  }

  const response = data as any;
  const paymentRecord = response?.payment;

  if (!paymentRecord) {
    throw new Error('Resposta inválida da função manualSubscription ao registrar pagamento.');
  }

  try {
    const { id: paymentId, ...paymentData } = paymentRecord;
    const updated = await databaseService.updateItem(
      APP_CONSTANTS.DATABASE.TABLES.PAYMENTS.NAME,
      paymentId,
      paymentData,
      { skipPendingOperation: true, source: 'remote' }
    );

    if (!updated) {
      await databaseService.createItem(
        APP_CONSTANTS.DATABASE.TABLES.PAYMENTS.NAME,
        paymentRecord,
        { skipPendingOperation: true, source: 'remote' }
      );
    }
  } catch (err) {
    localDbLogger.warn('[MANUAL] Erro ao sincronizar pagamento manual no banco local', err);
  }

  return {
    payment: paymentRecord,
  };
};

/**
 * Função auxiliar para buscar uma assinatura local pelo ID externo do Stripe
 * @param externalId ID da assinatura no Stripe
 * @param empresaId ID da empresa (opcional, recomendado para evitar colisões)
 * @returns Lista de assinaturas locais correspondentes
 */
export const getLocalSubscriptionByExternalId = async (
  externalId: string,
  empresaId?: string
): Promise<LocalSubscription[]> => {
  localDbLogger.info(`Buscando assinatura local pelo external_id: ${externalId}${empresaId ? `, empresa: ${empresaId}` : ''}`);

  const query: any = { external_subscription_id: externalId };

  // Se forneceu empresa_id, adicionar ao filtro
  if (empresaId) {
    query.empresa_id = empresaId;
  }

  const results = await databaseService.listItems(
    APP_CONSTANTS.DATABASE.TABLES.SUBSCRIPTIONS.NAME,
    query
  );

  localDbLogger.info(`Encontradas ${results?.length || 0} assinaturas locais com external_id: ${externalId}`);

  return (results || []) as LocalSubscription[];
};

export default {
  checkSubscriptionStatus,
  getAvailablePlans,
  getUserPayments,
  subscribeToPlan,
  subscribeWithStripe,
  subscribeWithApple,
  restoreApplePurchases,
  getLocalSubscriptionByExternalId,
  cancelSubscription,
  getSubscription,
  handleSuccess,
  createCustomer,
  listPrices,
  generateManualExternalId,
  upsertSubscriptionAdmin,
  registerManualPayment,
  activateTrialForCompany,
}; 
